import type { ReactNode } from "react"

interface StatTile {
  label: string
  value: string
  caption?: string
  /** Optional accent color class for the value */
  valueClass?: string
}

interface BigResultBannerProps {
  /** Tiny uppercase eyebrow label above the headline value */
  eyebrow: string
  /** The large headline value (e.g. "$89" or "22 cu ft") */
  headline: string
  /** Subtitle shown under the headline */
  subtitle?: string
  /** Up to 3 stat tiles shown to the right on desktop */
  stats?: StatTile[]
  /** Optional override: gradient classes (defaults to blue gradient) */
  gradientClass?: string
  /** Optional additional classes on the rounded banner */
  className?: string
}

/**
 * BigResultBanner — rounded gradient banner that showcases a single large
 * result value with up to 3 supporting stat tiles.
 *
 * Sits inside the standard max-w content wrapper (not edge-to-edge). Used
 * whenever a tool's primary output is a single number that deserves hero
 * treatment: annual $ cost, cu ft recommendation, break-even month, etc.
 */
export function BigResultBanner({
  eyebrow,
  headline,
  subtitle,
  stats = [],
  gradientClass = "bg-gradient-to-br from-[#0469f0] to-[#0053c4]",
  className = "",
}: BigResultBannerProps) {
  return (
    <div
      className={`rounded-2xl ${gradientClass} p-6 md:p-10 shadow-[0_8px_24px_rgba(4,105,240,0.18)] ${className}`}
    >
      <div className="grid md:grid-cols-3 gap-6 items-center text-white">
        <div className={stats.length > 0 ? "md:col-span-1" : "md:col-span-3"}>
          <p className="text-[11px] uppercase tracking-widest text-white/70 font-bold mb-1">
            {eyebrow}
          </p>
          <div className="text-6xl md:text-7xl font-black tracking-tighter leading-none">
            {headline}
          </div>
          {subtitle && (
            <p className="text-sm text-white/70 mt-2">{subtitle}</p>
          )}
        </div>
        {stats.length > 0 && (
          <div className="md:col-span-2 grid grid-cols-3 gap-3">
            {stats.slice(0, 3).map((s, i) => (
              <div
                key={i}
                className="rounded-lg bg-white/10 backdrop-blur-sm p-4 border border-white/15"
              >
                <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1">
                  {s.label}
                </p>
                <p className={`text-2xl font-black tracking-tighter ${s.valueClass ?? ""}`}>
                  {s.value}
                </p>
                {s.caption && (
                  <p className="text-xs text-white/60 mt-1">{s.caption}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export type { StatTile as BigResultBannerStat }
